import * as PIXI from 'pixi.js';
import type { DisplayObjectEvents } from 'pixi.js';
import {
    configure,
    createAtom,
    observable,
    reaction as mobxReaction,
    type IReactionDisposer,
    type IReactionOptions,
    type IReactionPublic,
} from 'mobx';
export {
    autorun,
    comparer,
    computed,
    createAtom,
    observable,
    onReactionError,
    runInAction,
    untracked,
} from 'mobx';
export type { IObservableValue, IReactionDisposer } from 'mobx';

// Luminary assigns observable properties from ordinary setters and load paths all over the codebase, which mobx's
// default `enforceActions: 'observed'` warns about once the property is observed — a single test file produces
// upwards of 60 warnings. This is warning suppression only: nothing depends on it for correctness, and the whole
// suite passes without it. Tighten this as classes move onto reactions and their writes become actions; until then
// mobx cannot flag a genuinely unsafe write, so reactions must not be assumed to be action-wrapped.
configure({ enforceActions: 'never' });

/**
 * mobx's `reaction` with `fireImmediately` defaulting to true, because a derivation that does not run until its
 * inputs next change leaves the object it derives unbuilt. Pass `{ fireImmediately: false }` to opt out.
 */
export function reaction<T, FireImmediately extends boolean = true>(
    expression: (r: IReactionPublic) => T,
    effect: (
        value: T,
        previousValue: FireImmediately extends true ? T | undefined : T,
        r: IReactionPublic,
    ) => void,
    opts?: IReactionOptions<T, FireImmediately>,
): IReactionDisposer {
    // the merged literal widens to boolean, which no longer matches the FireImmediately the caller pinned
    const options = { fireImmediately: true, ...opts } as IReactionOptions<T, FireImmediately>;
    return mobxReaction(expression, effect, options);
}

/**
 * Base class for objects that derive state reactively. Reactions registered through
 * [addReaction]{@link ReactiveBase#addReaction} are disposed when the object is destroyed.
 */
export class ReactiveBase extends PIXI.utils.EventEmitter<DisplayObjectEvents> {
    #disposers: IReactionDisposer[] = [];

    /**
     * Ties the lifetime of a mobx reaction to this object. Register from the constructor, after assigning every
     * property the reaction reads: `autorun` runs its callback immediately, so anything assigned on a later line is
     * not visible to that first run.
     */
    protected addReaction(disposer: IReactionDisposer): void {
        this.#disposers.push(disposer);
    }

    destroy(_args?: unknown): void {
        this.removeAllListeners();
        this.#disposers.forEach((dispose) => dispose());
        this.#disposers.length = 0;
    }
}

/**
 * Makes properties of the PIXI object stored in this accessor observable, so a derivation reading them re-runs when
 * they change — including when PIXI changes them itself, as `Resource.resize` does to `_width`, or
 * `DisplayObject.destroy` to `_destroyed`. Every object assigned to the field is instrumented, so replacing it keeps
 * working.
 *
 * Each named property is redefined on the object itself as an accessor mobx tracks. A data property moves into an
 * observable box. An accessor PIXI defines on the prototype keeps running, and notifies only when the value it
 * reports afterwards differs.
 *
 * The properties are strings because the ones worth observing are usually PIXI internals the types hide. Name only
 * what a derivation actually reads, and revisit the list when upgrading PIXI: a property PIXI removes throws on
 * assignment, but one it stops writing goes silently stale.
 *
 * @param properties - the properties to observe on each stored object
 * @param options.only - instrument only objects of this class, for a field that holds several kinds
 */
export function observesPixi(
    properties: readonly string[],
    options: { only?: abstract new (...args: never[]) => object } = {},
) {
    const instrument = (value: unknown) => {
        if (value === null || typeof value !== 'object') {
            return;
        }
        if (options.only && !(value instanceof options.only)) {
            return;
        }
        properties.forEach((property) => observePixiProperty(value, property));
    };
    return function <This, Value>(
        target: ClassAccessorDecoratorTarget<This, Value>,
        _context: ClassAccessorDecoratorContext<This, Value>,
    ): ClassAccessorDecoratorResult<This, Value> {
        return {
            init(value) {
                instrument(value);
                return value;
            },
            set(value) {
                instrument(value);
                target.set.call(this, value);
            },
        };
    };
}

/** The properties already made observable on each object, so storing it in a second field does not wrap them twice. */
const observedPixiProperties = new WeakMap<object, Set<string>>();

function observePixiProperty(target: object, property: string): void {
    const observed = observedPixiProperties.get(target) ?? new Set<string>();
    observedPixiProperties.set(target, observed);
    if (observed.has(property)) {
        return;
    }

    const inherited = findPropertyDescriptor(target, property);
    if (!inherited) {
        throw new Error(`Cannot observe ${property}: the object has no such property`);
    }
    observed.add(property);

    if (inherited.get || inherited.set) {
        const { get, set } = inherited;
        const atom = createAtom(`pixi.${property}`);
        Object.defineProperty(target, property, {
            get() {
                atom.reportObserved();
                return get?.call(target);
            },
            set(value: unknown) {
                const before = get?.call(target);
                set?.call(target, value);
                if (get?.call(target) !== before) {
                    atom.reportChanged();
                }
            },
            configurable: true,
            enumerable: inherited.enumerable,
        });
        return;
    }

    const box = observable.box((target as Record<string, unknown>)[property], {
        name: `pixi.${property}`,
        deep: false,
    });
    Object.defineProperty(target, property, {
        get: () => box.get(),
        set: (value: unknown) => box.set(value),
        configurable: true,
        enumerable: inherited.enumerable,
    });
}

function findPropertyDescriptor(target: object, property: string): PropertyDescriptor | undefined {
    for (let owner: object | null = target; owner; owner = Object.getPrototypeOf(owner)) {
        const descriptor = Object.getOwnPropertyDescriptor(owner, property);
        if (descriptor) {
            return descriptor;
        }
    }
    return undefined;
}
